import { Redirect, Stack, router } from 'expo-router'
import { Platform, StyleSheet } from 'react-native'
import * as Experiences from '@/src/experiences'
import { DesignSystem } from '@/src/design-system'
import { useEffect } from 'react'
import { useSessionKey } from '@/src/common/hooks/useSessionKey'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { Routes } from '@/src/common/enums/routes'
import { useSardine } from '@/src/services/Sardine'
import { isE2E } from '@/src/common/environment'
import * as Hooks from '../hooks'
import { HighnoteLayout } from './HighnoteLayout'
import { PlatformLayout } from './PlatformLayout'

// eslint-disable-next-line
export const unstable_settings = {
  // Ensure that reloading on `/modal` keeps a back button present.
  initialRouteName: 'index',
}

const useEnableClipboardTracking =
  // Disable clipboard tracking in E2E due to prompt
  isE2E ||
  // web doesn't support clipbaord tracking
  Platform.OS === 'web'
    ? () => {}
    : () => {
        const { updateOptions } = useSardine()
        useEffect(() => {
          void updateOptions({ enableClipboardTracking: true })
        }, [updateOptions])
      }

export function OnboardingLayout() {
  const Alert = useAlert(OnboardingLayout.name)
  const { sessionKey, previousSessionKey } = useSessionKey()
  const platform = Hooks.useCardApplicationPlatform()

  useEffect(() => {
    if (sessionKey) {
      if (previousSessionKey && sessionKey !== previousSessionKey) {
        Alert.alert(
          'Your Mesa Credit Card application session has expired',
          'Please start a new application.',
          [{ text: 'Ok', onPress: () => router.replace(Routes.CardBenefits.route) }],
          { closeableByOverlay: false, onDismiss: () => router.replace(Routes.CardBenefits.route) },
        )
      }
    }
  }, [sessionKey, previousSessionKey, Alert])

  useEnableClipboardTracking()

  switch (platform) {
    case 'LOADING':
      return <DesignSystem.Loading.Screen style={[StyleSheet.absoluteFill, { zIndex: 88 }]} />
    case 'ERROR':
    case 'APPLICATIONS_DISABLED':
      return <Redirect href="/main/(tabs)/card/cardBenefits" />
    case 'HIGHNOTE':
      return (
        <HighnoteLayout>
          <LayoutNav />
        </HighnoteLayout>
      )
    default:
      return (
        <PlatformLayout>
          <LayoutNav />
        </PlatformLayout>
      )
  }
}

function LayoutNav() {
  const cardApplicationRoutesEnabled =
    Experiences.CardApplication.Statsig.FeatureGate.useCardApplicationRoutesEnabled()

  if (!cardApplicationRoutesEnabled) {
    return <Redirect href="/main/(tabs)/card/cardBenefits" />
  }

  return (
    <>
      {typeof cardApplicationRoutesEnabled !== 'boolean' && (
        // King: This just covers stack screens, they are still mounted below the fold, beware if they have any side effect that trigger mutations or change application state you might have issues, weird experiences, or bugs. This pattern shouldn't be re used see https://github.com/Acre-Rewards/acre-app/pull/986#discussion_r2039880626
        <DesignSystem.Loading.Screen style={[StyleSheet.absoluteFill, { zIndex: 88 }]} />
      )}
      <Stack>
        <Stack.Screen name="index" options={{ headerShown: false }} />
        <Stack.Screen name="applicationApproved" options={{ headerShown: false }} />
        <Stack.Screen name="applicationCardHolderAgreement" options={{ headerShown: false }} />
        <Stack.Screen name="applicationConfirmation" options={{ headerShown: false }} />
        <Stack.Screen name="applicationCreditFrozen" options={{ headerShown: false }} />
        <Stack.Screen name="applicationDenied" options={{ headerShown: false }} />
        <Stack.Screen name="applicationInReview" options={{ headerShown: false }} />
        <Stack.Screen name="applicationManualReview" options={{ headerShown: false }} />
        <Stack.Screen name="applicationProcessing" options={{ headerShown: false }} />
        <Stack.Screen name="documentUpload" options={{ headerShown: false, animation: 'fade' }} />
        <Stack.Screen name="findYourHome" options={{ headerShown: false }} />
        <Stack.Screen name="lastQuestion" options={{ headerShown: false }} />
        <Stack.Screen name="offerCode" options={{ headerShown: false }} />
        <Stack.Screen name="verifyOtpCardApplication" options={{ headerShown: false }} />
      </Stack>
    </>
  )
}
